import {
    Component,
    EventEmitter,
    inject,
    Input,
    OnDestroy,
    Output,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatMenuModule } from '@angular/material/menu';
import { MatIconModule } from '@angular/material/icon';
import { Store } from '@ngrx/store';

import { RevisionDataService } from '@services/data/revision-data-service';
import { SnackbarService } from '@services/snackbar.service';
import { UserDataService } from '@services/data/user-data.service';
import { DocumentService } from '@services/api/document/document.service';
import { DocumentDataService } from '@services/data/document-data.service';
import { DiscrepancyDataService } from '@services/data/discrepancy-data.service';
import { DiscrepancyService } from '@services/api/discrepancy/discrepancy.service';
import { LoaderComponent } from '@shared/loader/loader.component';
import { TooltipComponent } from '@shared/tooltip/tooltip.component';
import { TextButtonComponent } from '@shared/button/text-button/text-button.component';
import { FlatButtonComponent } from '@shared/button/flat-button/flat-button.component';
import { RecallForEditDialogComponent } from '@main/dialogs/recall-for-edit-dialog/recall-for-edit-dialog.component';
import { CancelDiscrepancyDialogComponent } from '@main/dialogs/cancel-discrepancy-dialog/cancel-discrepancy-dialog.component';
import { DiscardDiscrepancyDialogComponent } from '@main/dialogs/discard-discrepancy-dialog/discard-discrepancy-dialog.component';
import { UnresolveDiscrepancyDialogComponent } from '@main/dialogs/unresolve-discrepancy-dialog/unresolve-discrepancy-dialog.component';
import { RecallDiscrepancyDialogComponent } from '@main/dialogs/recall-discrepancy-dialog/recall-discrepancy-dialog.component';
import { ConfirmFinalVersionDialogComponent } from '@main/dialogs/confirm-final-version-dialog/confirm-final-version-dialog.component';
import { FinalVersionDialogComponent } from '@main/dialogs/final-version-dialog/final-version-dialog.component';
import { UnsavedChangesDialogComponent } from '@features/dialogs/unsaved-changes-dialog/unsaved-changes-dialog.component';
import {
    DiscrepancyEditOptions,
    DiscrepancyDetail,
    DiscrepancyActionCode,
    UpdateDiscrepancyRequest,
    DiscrepancyTerminateType,
    TERMINATE_WITHOUT_RESOLUTION,
    TerminateTypeWithoutResolution,
} from '@models/discrepancy';
import { Role } from '@models/user';
import { SubscriptionList, SubscriptionListType } from '@helpers/subscription';
import { mapKeysToCamelCase } from '@helpers/string-utils';
import { refreshActionRelatedData } from 'app/store/actions/discrepancy.actions';
import { ResolutionDecision } from '@models/document/resolution-decision';
import { StopClickDirective } from '@directives/stop-click.directive';
import { ErrorResponse } from '@models/api.model';
import DATA_QA from '@automation/data-qa.json';

@Component({
    selector: 'app-footer-state-actions',
    template: `
        <div class="flex items-center gap-10">
            @if (canRecallForEdit()) {
                <app-tooltip
                    class="disc-tooltip"
                    [isEnabled]="
                        shouldShowActionTooltip(recallForEditDisabled())
                    ">
                    <app-text-button
                        trigger
                        (clicked)="onEditClick()"
                        [dataQa]="dataQa.footerRecallForEditDiscrepancy"
                        [disabled]="recallForEditDisabled()">
                        Recall
                    </app-text-button>
                    <span content>{{ getDefaultActionTooltipText() }}</span>
                </app-tooltip>
            }
            @if (canRecall()) {
                <app-tooltip
                    class="disc-tooltip"
                    [isEnabled]="shouldShowActionTooltip(recallDisabled())">
                    <app-text-button
                        trigger
                        (clicked)="onRecallToAwaitClick()"
                        [dataQa]="dataQa.footerRecallToAwaitDiscrepancy"
                        [disabled]="recallDisabled()">
                        Recall
                    </app-text-button>
                    <span content>{{ getDefaultActionTooltipText() }}</span>
                </app-tooltip>
            }
            @if (canCancel()) {
                <app-tooltip
                    class="disc-tooltip"
                    [isEnabled]="shouldShowActionTooltip(cancelDisabled())">
                    <app-flat-button
                        trigger
                        variant="tertiary"
                        [dataQa]="dataQa.footerCancelDiscrepancy"
                        [disabled]="cancelDisabled()"
                        [attr.aria-disabled]="cancelDisabled()"
                        [matMenuTriggerFor]="
                            cancelDisabled() ? null : cancelDiscrepancyMenu
                        "
                        (menuOpened)="menuOpened = true"
                        (menuClosed)="menuOpened = false">
                        Cancel Discrepancy
                        <mat-icon
                            [svgIcon]="
                                menuOpened ? 'chevron-up' : 'chevron-down'
                            " />
                    </app-flat-button>
                    <span content>{{ getDefaultActionTooltipText() }}</span>
                </app-tooltip>
                <mat-menu #cancelDiscrepancyMenu="matMenu">
                    <div wfeStopClick>
                        <button
                            mat-menu-item
                            [attr.data-qa]="
                                dataQa.footerCancelDiscrepancyMarkAsCancelled
                            "
                            (click)="onCancelClick()">
                            Mark as Cancelled
                        </button>
                        <button
                            mat-menu-item
                            [attr.data-qa]="
                                dataQa.footerCancelDiscrepancyMarkAsUnresolved
                            "
                            (click)="onUnresolveClick()">
                            Mark as Unresolved
                        </button>
                    </div>
                </mat-menu>
            }
            @if (showCloseResolve()) {
                <app-tooltip
                    class="disc-tooltip"
                    [isEnabled]="
                        shouldShowActionTooltip(closeResolveDisabled())
                    ">
                    <app-flat-button
                        trigger
                        variant="cta"
                        [dataQa]="dataQa.footerCloseResolveDiscrepancy"
                        (clicked)="onCloseResolveClick()"
                        [disabled]="closeResolveDisabled()">
                        Close Resolve
                    </app-flat-button>
                    <span content>{{ getDefaultActionTooltipText() }}</span>
                </app-tooltip>
            }
            @if (discDetail.isDraft) {
                <app-tooltip
                    class="disc-tooltip"
                    [isEnabled]="shouldShowActionTooltip(discardDisabled())">
                    <app-flat-button
                        trigger
                        [disabled]="discardDisabled()"
                        (clicked)="onDiscard()"
                        [dataQa]="dataQa.footerDiscardDiscrepancy">
                        Discard
                    </app-flat-button>
                    <span content>{{ getDefaultActionTooltipText() }}</span>
                </app-tooltip>
            }
            @if (discDetail.status === 'Initial Review') {
                <app-tooltip
                    class="disc-tooltip"
                    [isEnabled]="shouldShowSaveTooltip()">
                    <app-flat-button
                        trigger
                        variant="tertiary"
                        [disabled]="saveDisabled()"
                        [dataQa]="dataQa.footerSaveDiscrepancy"
                        (clicked)="onSave()">
                        Save
                    </app-flat-button>
                    <span content>{{ getSaveTooltipText() }}</span>
                </app-tooltip>
            }
            @if (showNotify()) {
                <app-tooltip
                    class="disc-tooltip"
                    [isEnabled]="shouldShowActionTooltip(notifyDisabled())">
                    <app-flat-button
                        trigger
                        variant="cta"
                        [disabled]="notifyDisabled()"
                        [dataQa]="dataQa.footerNotifyDiscrepancy"
                        (clicked)="onNotifyClicked()">
                        Notify
                    </app-flat-button>
                    <span content>{{ getNotifyTooltipText() }}</span>
                </app-tooltip>
            }
            @if (showRenotify()) {
                <app-tooltip
                    class="disc-tooltip"
                    [isEnabled]="
                        shouldShowActionTooltip(reNotifyDisabledFinal())
                    ">
                    <app-flat-button
                        trigger
                        variant="cta"
                        [dataQa]="dataQa.footerRenotifyDiscrepancy"
                        (clicked)="onRenotify()"
                        [disabled]="
                            isSecondaryCollaborator || reNotifyDisabledFinal()
                        ">
                        Re-Notify
                    </app-flat-button>
                    <span content>{{ getReNotifyTooltipText() }}</span>
                </app-tooltip>
            }
        </div>
        @if (loading) {
            <app-loader type="overlay" size="large" position="fixed" />
        }
    `,
    imports: [
        MatMenuModule,
        MatIconModule,
        TextButtonComponent,
        FlatButtonComponent,
        TooltipComponent,
        LoaderComponent,
        StopClickDirective,
    ],
})
export class FooterStateActionsComponent implements OnDestroy {
    @Input({ required: true }) discDetail!: DiscrepancyDetail;
    @Input({ required: true }) canSave!: boolean;
    @Input({ required: true }) canNotify!: boolean;
    @Input({ required: true }) isAdmin!: boolean;
    @Input() isSecondaryCollaborator = false;
    @Input({ required: true }) reNotifyDisabled!: boolean;
    @Input({ required: true }) editForm!: FormGroup;
    @Input() editFormChanged = false;
    @Input({ required: true }) editOptions!: DiscrepancyEditOptions;
    @Input() isRecordsManager = false;
    /**
     * True when the current user is allowed to take (or be assigned) this
     * discrepancy. Used so a disabled button only suggests "Please take..."
     * when taking is actually possible; otherwise it explains the action is
     * blocked because the discrepancy belongs to another analyst.
     */
    @Input() canTakeDiscrepancy = false;
    /**
     * True when the parent footer-actions wrapper tooltip is active. While it
     * is active the wrapper is the single authority for explaining why actions
     * are blocked, so every per-button tooltip here must stay suppressed to
     * avoid two conflicting messages showing at the same time.
     */
    @Input() parentTooltipEnabled = false;
    @Input({ required: true }) isRmInactive!: boolean;
    @Input()
    normalizeCustomColumnValues: (
        customColumns?: Record<string, unknown>,
    ) => Record<string, unknown> = () => ({});
    @Output() saveFailed = new EventEmitter<ErrorResponse>();

    menuOpened = false;
    loading = false;
    readonly dataQa = DATA_QA;
    private _subscriptions = new SubscriptionList() as SubscriptionListType;
    resolutionDecision: ResolutionDecision | null = null;
    readonly blockedActionTooltipText =
        'This action is only available to the primary analyst.';
    readonly inactiveRecordsManagerTooltipText =
        'You cannot perform actions because this discrepancy is assigned to another analyst.';
    readonly invalidFormTooltipText = 'Please fix the errors to proceed.';

    private readonly documentService = inject(DocumentService);
    private readonly snackbarService = inject(SnackbarService);
    private readonly discrepancyService = inject(DiscrepancyService);
    private readonly userDataService = inject(UserDataService);
    private readonly documentDataService = inject(DocumentDataService);
    private readonly revisionDataService = inject(RevisionDataService);
    private readonly discrepancyDataService = inject(DiscrepancyDataService);
    private readonly dialog = inject(MatDialog);
    private readonly store = inject(Store);

    ngOnDestroy(): void {
        this._subscriptions.unsubscribeAllSafe();
    }

    get showAdminActions() {
        return this.isAdmin && !this.isRecordsManager;
    }

    recallForEditDisabled() {
        return (
            this.isSecondaryCollaborator ||
            this.isRmInactive ||
            !this.actionsEnabled()
        );
    }

    recallDisabled() {
        return (
            this.isSecondaryCollaborator ||
            this.isRmInactive ||
            !this.actionsEnabled()
        );
    }

    cancelDisabled() {
        return (
            this.isSecondaryCollaborator ||
            this.isRmInactive ||
            !this.actionsEnabled()
        );
    }

    closeResolveDisabled() {
        return (
            this.isSecondaryCollaborator ||
            this.isRmInactive ||
            !this.canCloseResolve()
        );
    }

    discardDisabled() {
        return (
            this.isSecondaryCollaborator ||
            this.isRmInactive ||
            !this.actionsEnabled()
        );
    }

    saveDisabled() {
        return !this.canSave;
    }

    notifyDisabled() {
        return (
            this.isSecondaryCollaborator ||
            !this.canNotify ||
            this.discDetail.analyst === null
        );
    }

    shouldShowActionTooltip(isDisabled: boolean) {
        return isDisabled && !this.parentTooltipEnabled;
    }

    shouldShowSaveTooltip() {
        if (!this.saveDisabled() || this.parentTooltipEnabled) {
            return false;
        }

        if (this.isSecondaryCollaborator || this.isRmInactive) {
            return true;
        }

        if (!this.actionsEnabled()) {
            return true;
        }

        return this.editForm.invalid;
    }

    getDefaultActionTooltipText() {
        if (this.isSecondaryCollaborator) {
            return this.blockedActionTooltipText;
        }

        if (this.isRmInactive) {
            return this.inactiveRecordsManagerTooltipText;
        }

        if (this.discDetail.analyst === null) {
            return this.getTakeTooltipText();
        }

        return this.canTakeDiscrepancy
            ? this.getTakeTooltipText()
            : this.inactiveRecordsManagerTooltipText;
    }

    getSaveTooltipText() {
        if (!this.shouldShowSaveTooltip()) {
            return '';
        }

        if (this.isSecondaryCollaborator) {
            return this.blockedActionTooltipText;
        }

        if (this.isRmInactive) {
            return this.inactiveRecordsManagerTooltipText;
        }

        if (!this.actionsEnabled()) {
            return this.takeOrBlockedTooltipText();
        }

        return this.invalidFormTooltipText;
    }

    getNotifyTooltipText() {
        if (this.isSecondaryCollaborator) {
            return this.blockedActionTooltipText;
        }

        if (this.isRmInactive) {
            return this.inactiveRecordsManagerTooltipText;
        }

        if (this.discDetail.analyst === null) {
            return this.getTakeTooltipText();
        }

        if (!this.actionsEnabled()) {
            return this.takeOrBlockedTooltipText();
        }

        return this.invalidFormTooltipText;
    }

    getReNotifyTooltipText() {
        if (this.isSecondaryCollaborator) {
            return this.blockedActionTooltipText;
        }

        if (this.isRmInactive) {
            return this.inactiveRecordsManagerTooltipText;
        }

        if (this.discDetail.analyst === null) {
            return this.getTakeTooltipText();
        }

        if (!this.actionsEnabled()) {
            return this.takeOrBlockedTooltipText();
        }

        return this.invalidFormTooltipText;
    }

    private takeOrBlockedTooltipText() {
        return this.canTakeDiscrepancy
            ? this.getTakeTooltipText()
            : this.inactiveRecordsManagerTooltipText;
    }

    private getTakeTooltipText() {
        return `Please take${this.showAdminActions ? ' or assign' : ''} the discrepancy to enable actions.`;
    }

    canRecallForEdit() {
        return this.discDetail.actions.some(
            (item) => item.action === DiscrepancyActionCode.RECALL_FOR_EDIT,
        );
    }

    onEditClick() {
        if (this.isSecondaryCollaborator) {
            return;
        }

        this.dialog.open(RecallForEditDialogComponent, {
            data: {
                id: this.discDetail?.id,
                discrepancyNo: this.discDetail.discrepancyNo,
            },
            maxWidth: '450px',
            minWidth: '200px',
            width: '450px',
            disableClose: true,
        });
    }

    canRecall() {
        return this.discDetail.actions.some(
            (item) => item.action === DiscrepancyActionCode.RECALL,
        );
    }

    onRecallToAwaitClick() {
        if (this.isSecondaryCollaborator) {
            return;
        }

        this.dialog.open(RecallDiscrepancyDialogComponent, {
            data: {
                id: this.discDetail?.id,
                discrepancyNo: this.discDetail.discrepancyNo,
            },
            maxWidth: '450px',
            minWidth: '200px',
            width: '450px',
        });
    }

    actionsEnabled() {
        if (this.discDetail.analyst === null)
            return this.userDataService.currentUser?.role === Role.Host_Admin;
        else {
            return this.showAdminActions || this.isOwner();
        }
    }

    isOwner() {
        return (
            this.userDataService.currentUser?.id ===
            this.discDetail.analyst?.backingField
        );
    }

    reNotifyDisabledFinal() {
        if (this.discDetail.analyst === null) return true;
        return (
            this.isRmInactive || this.reNotifyDisabled || !this.actionsEnabled()
        );
    }

    canCancel() {
        return (
            !this.discDetail.isDraft &&
            this.discDetail.actions.some(
                (item) => item.action === DiscrepancyActionCode.CANCEL,
            )
        );
    }

    onCancelClick() {
        if (this.isSecondaryCollaborator) {
            return;
        }

        this.getResolutionDecisionAndExecute(() => {
            this.dialog.open(CancelDiscrepancyDialogComponent, {
                data: {
                    id: this.discDetail?.id,
                    discrepancyNo: this.discDetail.discrepancyNo,
                },
                maxWidth: '450px',
                minWidth: '200px',
                width: '450px',
            });
        }, 'Cancel');
    }

    onUnresolveClick() {
        if (this.isSecondaryCollaborator) {
            return;
        }

        this.getResolutionDecisionAndExecute(() => {
            this.dialog.open(UnresolveDiscrepancyDialogComponent, {
                data: {
                    id: this.discDetail?.id,
                    discrepancyNo: this.discDetail.discrepancyNo,
                },
                maxWidth: '450px',
                minWidth: '200px',
                width: '450px',
                maxHeight: '90vh',
            });
        }, 'Unresolve');
    }

    showCloseResolve() {
        return this.discDetail.actions.some(
            (item) => item.action === DiscrepancyActionCode.CLOSE_RESOLVE,
        );
    }

    canCloseResolve() {
        return this.showAdminActions || this.isOwner();
    }

    onCloseResolveClick() {
        if (
            this.isSecondaryCollaborator ||
            this.isRmInactive ||
            !this.canCloseResolve()
        ) {
            return;
        }

        this.getResolutionDecisionAndExecute(() => {
            this.closeResolveDiscrepancy();
        }, 'Close Resolve');
    }

    showNotify() {
        return this.discDetail.actions.some(
            (item) => item.action === DiscrepancyActionCode.NOTIFY,
        );
    }

    showRenotify() {
        return this.discDetail.actions.some(
            (item) => item.action === DiscrepancyActionCode.RENOTIFY,
        );
    }

    onDiscard() {
        if (this.isSecondaryCollaborator) {
            return;
        }

        this.getResolutionDecisionAndExecute(() => {
            this.dialog.open(DiscardDiscrepancyDialogComponent, {
                data: {
                    id: this.discDetail?.id,
                    discrepancyNo: this.discDetail.discrepancyNo,
                },
                maxWidth: '450px',
                minWidth: '200px',
                width: '450px',
            });
        }, 'Discard');
    }

    onSave(triggerNotify = false) {
        this.loading = true;
        const { id, discrepancyNo } = this.discDetail;
        const editData = this.getUpdatedDiscrepancyData();

        this._subscriptions['edit-discrepancy'] = this.discrepancyService
            .editDiscrepancy(id, editData)
            .subscribe({
                next: (result) => {
                    if (result.success) {
                        this.snackbarService.success({
                            variant: 'success',
                            header: `Discrepancy ${discrepancyNo} is Saved`,
                        });
                        this.discrepancyDataService.triggerDiscrepancySaved();
                        if (triggerNotify) {
                            this.notifyDiscrepancy();
                        } else {
                            this.store.dispatch(refreshActionRelatedData());
                        }
                    } else {
                        this.saveFailed.emit(result.error);
                        if (
                            result.error.status === 409 &&
                            result.error.title === 'Conflict'
                        ) {
                            this.loading = false;
                            return;
                        }
                        this.snackbarService.error({
                            variant: 'error',
                            header: `Discrepancy ${discrepancyNo} Could Not be Saved`,
                            content: result.error?.detail,
                        });
                        this.loading = false;
                    }
                },
                complete: () => {
                    if (!triggerNotify) this.loading = false;
                },
            });
    }

    onNotifyClicked() {
        if (
            this.isSecondaryCollaborator ||
            !this.canNotify ||
            this.discDetail.analyst === null
        ) {
            return;
        }

        const { unsavedDocumentId, originalDocument } =
            this.revisionDataService;
        const isOriginalUnsaved =
            unsavedDocumentId && originalDocument?.id === unsavedDocumentId;

        if (isOriginalUnsaved) {
            this._subscriptions['original-saved'] =
                this.revisionDataService.originalSaved$.subscribe({
                    next: () => this.notify(),
                });

            this.dialog
                .open(UnsavedChangesDialogComponent, {
                    data: {
                        unsavedChangeArea: `${
                            this.revisionDataService.originalDocument
                                ?.revisionName
                        } (Original)`,
                        action: 'notifying station',
                    },
                    maxWidth: '430px',
                    minWidth: '200px',
                    width: '430px',
                })
                .afterClosed()
                .subscribe({
                    next: (result) => {
                        if (result.save) {
                            this.revisionDataService.triggerRevisionSave();
                        } else if (result.leave) {
                            this.notify();
                        }
                    },
                });
        } else {
            this.notify();
        }
    }

    onRenotify() {
        if (this.isSecondaryCollaborator) {
            return;
        }

        const { id, discrepancyNo } = this.discDetail;
        this.loading = true;

        this._subscriptions['renotify-discrepancy'] = this.discrepancyService
            .renotifyDiscrepancy(id)
            .subscribe({
                next: (success) => {
                    if (success) {
                        this.snackbarService.success({
                            variant: 'success',
                            header: `Re-Notified for Discrepancy ${discrepancyNo}!`,
                        });
                        this.store.dispatch(refreshActionRelatedData());
                    }
                },
                complete: () => (this.loading = false),
            });
    }

    private getUpdatedDiscrepancyData() {
        const { customColumns, ...formValues } = (this.editForm?.value ??
            {}) as {
            customColumns?: Record<string, unknown>;
            [key: string]: unknown;
        };
        const normalizedCustomColumns =
            this.normalizeCustomColumnValues(customColumns);
        const camelCaseCustomColumns = mapKeysToCamelCase(
            normalizedCustomColumns,
        );

        return {
            ...new UpdateDiscrepancyRequest(
                this.discDetail.priority,
                this.discDetail.reason?.backingField,
                this.discDetail.pageRange,
                this.discDetail.station?.backingField,
                this.discDetail.directions,
                this.discDetail.notifyWithOnlySelectedPages,
                this.discDetail.primaryFixer?.id,
            ),
            ...formValues,
            ...camelCaseCustomColumns,
        };
    }

    private notify() {
        if (this.canSave) {
            this.onSave(true);
        } else {
            this.notifyDiscrepancy();
        }
    }

    private openFinalVersionDialog(triggerType: DiscrepancyTerminateType) {
        this.dialog.open(FinalVersionDialogComponent, {
            data: {
                triggerType,
                id: this.discDetail?.id,
                discrepancyNo: this.discDetail.discrepancyNo,
            },
            maxWidth: '100%',
            minWidth: '200px',
            width: '100%',
            height: '100%',
            panelClass: 'p-4',
        });
    }

    private openConfirmFinalVersionDialog(
        triggerType: TerminateTypeWithoutResolution,
    ) {
        const { id, discrepancyNo } = this.discDetail;
        const documentNameToPublish =
            this.revisionDataService.originalDocument?.revisionName;

        this.dialog.open(ConfirmFinalVersionDialogComponent, {
            data: { triggerType, id, discrepancyNo, documentNameToPublish },
            maxWidth: '522px',
            minWidth: '200px',
            width: '522px',
        });
    }

    private closeResolveDiscrepancy() {
        const { id, discrepancyNo } = this.discDetail;
        this.loading = true;

        this._subscriptions['close-resolve-disc'] = this.discrepancyService
            .closeResolveDiscrepancy(id)
            .subscribe({
                next: (success) => {
                    if (success) {
                        this.snackbarService.success({
                            variant: 'success',
                            header: `Discrepancy ${discrepancyNo} is Close-Resolved and moved to "Recently Completed"!`,
                        });
                        this.store.dispatch(refreshActionRelatedData());
                    }
                },
                complete: () => (this.loading = false),
            });
    }

    private notifyDiscrepancy() {
        const { id, discrepancyNo } = this.discDetail;
        this.loading = true;

        this._subscriptions['notify-discrepancy'] = this.discrepancyService
            .notifyDiscrepancy(id)
            .subscribe({
                next: (success) => {
                    if (success) {
                        this.snackbarService.success({
                            variant: 'success',
                            header: `Notified for Discrepancy ${discrepancyNo}!`,
                        });
                        this.store.dispatch(refreshActionRelatedData());
                    } else {
                        this.snackbarService.error({
                            variant: 'error',
                            header: `Discrepancy ${discrepancyNo} Could Not be Notified`,
                        });
                    }
                },
                complete: () => (this.loading = false),
            });
    }

    private getResolutionDecisionAndExecute(
        action: () => void,
        triggerType: DiscrepancyTerminateType,
    ) {
        const execute = () => {
            if (this.resolutionDecision) {
                const { isReadyForFinal, useOriginalAsFinal } =
                    this.resolutionDecision;

                if (isReadyForFinal) {
                    this.openFinalVersionDialog(triggerType);
                } else {
                    const noResolution =
                        TERMINATE_WITHOUT_RESOLUTION.includes(triggerType);

                    if (noResolution && useOriginalAsFinal) {
                        this.openConfirmFinalVersionDialog(
                            triggerType as TerminateTypeWithoutResolution,
                        );
                    } else {
                        action();
                    }
                }
            }
        };

        if (this.resolutionDecision) {
            execute();
        } else {
            const documentId = this.documentDataService.currentDocument?.id;

            if (documentId)
                this._subscriptions['resolutio-decision'] = this.documentService
                    .getResolutionDecision(documentId, {
                        discrepancy: this.discDetail.id,
                    })
                    .subscribe({
                        next: (data) => {
                            this.resolutionDecision = data;
                            execute();
                        },
                    });
        }
    }
}
